import { useState } from "react"
import type { ReactNode } from "react"

import type { PlanSectionView } from "./plan-utils"

import { ExpandModal } from "./expand-modal"
import { DataGapsCallout, FreshDraftBanner } from "./plan-callouts"
import { PlanTile } from "./plan-tile"

/**
 * Canvas view: a fixed-tile grid mirroring the mockup structure (row/col spans).
 * Each tile clips overflow and scrolls internally; Expand opens the full section
 * in a modal. Canvas citation chips are inert (the Document view owns Sources).
 */
export function PlanCanvasView({
  sections,
  renderBody,
  dataGaps,
  freshDraft,
  canEdit,
  onUpdate,
  updateTestId,
}: {
  sections: PlanSectionView[]
  /** Canvas passes no citation handler → chips render inert. */
  renderBody: (fieldKey: string) => ReactNode
  dataGaps: string[]
  freshDraft: boolean
  canEdit: boolean
  onUpdate: (fieldKey: string, title: string) => void
  updateTestId: (fieldKey: string) => string
}) {
  const [expanded, setExpanded] = useState<PlanSectionView | null>(null)

  return (
    <div className="space-y-4">
      {freshDraft ? <FreshDraftBanner /> : null}
      <DataGapsCallout labels={dataGaps} />
      <div className="grid auto-rows-[minmax(220px,220px)] grid-cols-1 gap-4 md:grid-cols-12">
        {sections.map((section) => (
          <PlanTile
            key={section.no}
            canEdit={canEdit}
            colSpan={section.colSpan}
            mode="canvas"
            no={section.no}
            rowSpan={section.rowSpan}
            title={section.title}
            updatable={section.updatable}
            updateTestId={updateTestId(section.fieldKey)}
            onExpand={() => setExpanded(section)}
            onUpdate={
              section.updatable ? () => onUpdate(section.fieldKey, section.title) : undefined
            }
          >
            {renderBody(section.fieldKey)}
          </PlanTile>
        ))}
      </div>
      <ExpandModal
        canUpdate={Boolean(expanded?.updatable && canEdit)}
        open={Boolean(expanded)}
        title={expanded ? `${expanded.no} ${expanded.title}` : ""}
        updateTestId={expanded ? `${updateTestId(expanded.fieldKey)}-expand` : undefined}
        onOpenChange={(open) => !open && setExpanded(null)}
        onUpdate={
          expanded?.updatable ? () => onUpdate(expanded.fieldKey, expanded.title) : undefined
        }
      >
        {expanded ? renderBody(expanded.fieldKey) : null}
      </ExpandModal>
    </div>
  )
}
